<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="swiper c-image-text-card__swiper" ref="swiperEl">
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in items"
            :key="item.id || index"
            class="swiper-slide c-image-text-card__slide"
          >
            <div class="c-image-text-card__inner">
              <div class="c-image-text-card__media">
                <AdaptiveImage
                  v-if="item.fields?.image"
                  class="c-image-text-card__image"
                  :field="item.fields.image"
                  :show="true"
                  :enable-scroll-detection="false"
                  :show-image-redius="false"
                />
              </div>

              <component
                :is="getItemLink(item).href ? 'a' : 'div'"
                class="c-image-text-card__content"
                :href="getItemLink(item).href || undefined"
                :target="getItemLink(item).target"
                :rel="getItemLink(item).target === '_blank' ? 'noopener noreferrer' : undefined"
                :aria-label="getItemLink(item).href ? getItemLink(item).label : undefined"
              >
                <JssText
                  v-if="item.fields?.title?.value"
                  class="c-image-text-card__title"
                  :field="item.fields.title"
                />
                <RichText
                  v-if="item.fields?.description?.value"
                  class="c-image-text-card__description"
                  :field="item.fields.description"
                />
              </component>
            </div>
          </div>
        </div>
      </div>
      <div
        class="c-image-text-card__pagination swiper-pagination"
        ref="paginationEl"
        v-show="items.length > 1"
      />
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import { destroySwiper } from '@/utils/site-utils';
import { ImageTextCardProps } from '../../types/components/image-text-card';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import 'swiper/css';
import utils from '@/utils';

const props = defineProps<ImageTextCardProps>();
const fields = computed(() => props.fields);
const items = computed(() => props.fields?.items ?? []);
const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);

let swiper: Swiper | null = null;

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-image-text-card'));

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});

const getItemLink = (item: (typeof items.value)[number]) => {
  const value = item.fields?.link?.value || {};
  return {
    href: value.href || '',
    target: value.target,
    label: value.text || value.title || item.fields?.title?.value || 'View details',
  };
};

const buildSwiper = () => {
  if (!swiperEl.value || !paginationEl.value || !items.value.length) return;

  destroySwiper(swiper, true, true);
  swiper = new Swiper(swiperEl.value, {
    slidesPerView: 1,
    observer: true,
    observeParents: true,
    modules: [Pagination],
    pagination: {
      el: paginationEl.value,
      clickable: true,
      bulletElement: 'span',
    },
  });
};

onMounted(() => {
  setTimeout(() => nextTick(() => buildSwiper()), 50);
});

watch(
  () => items.value.length,
  (newLen, oldLen) => {
    if (newLen > 0 && newLen !== oldLen) {
      nextTick(() => buildSwiper());
    }
  }
);

onBeforeUnmount(() => {
  destroySwiper(swiper, true, true);
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-image-text-card {
  padding: 40px 0;
  &__swiper {
    width: 100%;
    overflow: hidden;
  }

  &__slide {
    height: auto;
  }

  &__inner {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    max-width: 1440px;
    margin: 0 auto;
  }

  &__media {
    position: relative;
    z-index: 2;
    overflow: hidden;
    border-radius: 8px;
    aspect-ratio: 1 / 1;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 360px;
    padding: 32px;
    overflow: visible;
    color: inherit;
    text-decoration: none;

    &[href] {
      cursor: pointer;
    }

    &:focus-visible {
      outline: 2px solid #739b78;
      outline-offset: -4px;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      z-index: 0;
      pointer-events: none;
    }

    &::before {
      inset: -44px 0 0;
      background: #f3f6f3;
    }

    &::after {
      right: 0;
      bottom: 0;
      width: 42%;
      height: 80%;
      background: $white;
      clip-path: polygon(100% 0, 100% 100%, 0 100%);
    }
  }

  &__title,
  &__description {
    position: relative;
    z-index: 1;
  }

  &__title {
    margin: 0;
    color: #404040;
    font-size: 32px;
    line-height: 1.18;
    font-weight: 600;
  }

  &__description {
    margin-top: 22px;
    color: #404040;
    font-size: 14px;
    line-height: 1.45;
  }

  &__pagination {
    position: static;
    display: flex;
    justify-content: center;
    gap: 4px;
    margin-top: 60px;

    .swiper-pagination-bullet {
      width: 24px;
      height: 10px;
      margin: 0;
      border-radius: 0 2px 0 2px;
      background: #d8dfd8;
      opacity: 1;
      transform: skewX(-45deg);
      transition: background-color 0.2s ease;
    }

    .swiper-pagination-bullet-active {
      background: #739b78;
    }
  }

  @include tablet {
    &__inner {
      grid-template-columns: minmax(320px, 440px) 1fr;
      align-items: center;
    }

    &__media {
      aspect-ratio: 1 / 1;
    }

    &__content {
      margin-top: 0;
      margin-left: -72px;
      min-height: 360px;
      padding: 0 120px 0 144px;

      &::before,
      &::after {
        top: 50%;
        bottom: auto;
        height: 360px;
        transform: translateY(-50%);
      }

      &::before {
        border-radius: 0 18px 0 0;
      }

      &::after {
        right: 0;
        width: 42%;
        background: $white;
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
        border-top-right-radius: 18px;
      }
    }
  }

  @include tablet-landscape {
    &__content {
      padding: 0 30px 0 164px;
    }

    &__title {
      font-size: 44px;
    }
  }
}
</style>
